import { useFormik } from "formik";
import { BreadCrumb } from "primereact/breadcrumb";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { MultiSelect } from "primereact/multiselect";
import { classNames } from "primereact/utils";
import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import SectionHeading from "../../../app/common/SectionHeading/SectionHeading";
import { RootStoreContext } from "../../../app/stores/rootStore";
import { appColors } from "../../../colors";
import agent from "../../../app/api/agent";

const ProjectCreate = () => {
  const navigate = useNavigate();

  const rootStore = useContext(RootStoreContext);
  const { appVars } = rootStore.generalStore;
  const { createUnlinkedProject, creatingUnlinkedProject } =
    rootStore.projectStore;

  const [userOptions, setUserOptions] = useState([]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const users = await agent.Admin.userList();
        setUserOptions(
          users.map((u) => ({
            label: `${u.displayName || u.email} (${u.email})`,
            value: u.email,
          }))
        );
      } catch (error) {
        console.error("Failed to fetch users", error);
      }
    };
    fetchUsers();
  }, []);

  const breadCrumbItems = [
    {
      label: "Project Management",
      command: () => {
        navigate("/pm/");
      },
    },
    {
      label: "Projects",
      command: () => {
        navigate("/pm/project/");
      },
    },
    {
      label: "Create Project",
    },
  ];

  const formik = useFormik({
    initialValues: {
      projectName: "",
      targetName: "",
      modality: null,
      primaryOrg: null,
      managerEmail: null,
      participantEmails: [],
    },
    validate: (data) => {
      let errors = {};
      if (!data.projectName) {
        errors.projectName = "Project name is required.";
      }
      if (!data.primaryOrg) {
        errors.primaryOrg = "Primary organization is required.";
      }
      if (!data.modality) {
        errors.modality = "Modality is required.";
      }
      return errors;
    },
    onSubmit: (data) => {
      const payload = {
        projectName: data.projectName,
        targetName: data.targetName || "",
        modality: data.modality,
        primaryOrg: data.primaryOrg,
        supportingOrgs: [],
        strainId: "00000000-0000-0000-0000-000000000000",
        managerEmail: data.managerEmail || null,
        participantEmails: data.participantEmails || [],
      };
      createUnlinkedProject(payload).then((res) => {
        if (res !== null) {
          navigate("/pm/project/");
        }
      });
    },
  });

  const isFormFieldValid = (field) =>
    !!(formik.touched[field] && formik.errors[field]);
  const getFormErrorMessage = (field) => {
    return (
      isFormFieldValid(field) && (
        <small className="p-error">{formik.errors[field]}</small>
      )
    );
  };

  return (
    <div className="flex flex-column w-full">
      <div className="flex w-full pb-2">
        <BreadCrumb model={breadCrumbItems} />
      </div>

      <div className="flex w-full">
        <SectionHeading
          icon="icon icon-common icon-plus"
          heading="New Project"
          color={appColors.sectionHeadingBg.project}
        />
      </div>

      <div className="flex w-full justify-content-center">
        <div className="card">
          <form onSubmit={formik.handleSubmit} className="p-fluid">
            <div className="flex gap-4">
              <div className="flex flex-column" style={{ minWidth: "500px" }}>
                <h2>Project Details</h2>

                <div className="field">
                  <label
                    htmlFor="projectName"
                    className={classNames({
                      "p-error": isFormFieldValid("projectName"),
                    })}
                  >
                    Project Name *
                  </label>
                  <InputText
                    id="projectName"
                    name="projectName"
                    value={formik.values.projectName}
                    onChange={formik.handleChange}
                    autoFocus
                    className={classNames({
                      "p-invalid": isFormFieldValid("projectName"),
                    })}
                  />
                  {getFormErrorMessage("projectName")}
                </div>

                <div className="field">
                  <label htmlFor="targetName">Target Name</label>
                  <InputText
                    id="targetName"
                    name="targetName"
                    value={formik.values.targetName}
                    onChange={formik.handleChange}
                    placeholder="e.g. InhA, KatG, or strain name (M. tuberculosis H37Rv)"
                  />
                </div>

                <div className="field">
                  <label
                    htmlFor="modality"
                    className={classNames({
                      "p-error": isFormFieldValid("modality"),
                    })}
                  >
                    Modality *
                  </label>
                  <Dropdown
                    id="modality"
                    name="modality"
                    value={formik.values.modality}
                    options={[
                      { label: "Small Molecule", value: "Small Molecule" },
                      { label: "RIPTAC", value: "RIPTAC" },
                      { label: "PROTAC", value: "PROTAC" },
                      { label: "ADC", value: "ADC" },
                      { label: "Antibody", value: "Antibody" },
                    ]}
                    onChange={(e) =>
                      formik.setFieldValue("modality", e.value)
                    }
                    placeholder="Select modality"
                    className={classNames({
                      "p-invalid": isFormFieldValid("modality"),
                    })}
                  />
                  {getFormErrorMessage("modality")}
                </div>

                <div className="field">
                  <label
                    htmlFor="primaryOrg"
                    className={classNames({
                      "p-error": isFormFieldValid("primaryOrg"),
                    })}
                  >
                    Primary Organization *
                  </label>
                  <Dropdown
                    value={formik.values.primaryOrg}
                    options={appVars?.appOrgs || []}
                    onChange={(e) =>
                      formik.setFieldValue("primaryOrg", e.value)
                    }
                    optionLabel="alias"
                    placeholder="Select primary organization"
                    filter
                    showClear
                    filterBy="alias"
                    className={classNames({
                      "p-invalid": isFormFieldValid("primaryOrg"),
                    })}
                  />
                  {getFormErrorMessage("primaryOrg")}
                </div>

                <div className="field">
                  <label htmlFor="managerEmail">Project Manager</label>
                  <Dropdown
                    value={formik.values.managerEmail}
                    options={userOptions}
                    onChange={(e) =>
                      formik.setFieldValue("managerEmail", e.value)
                    }
                    placeholder="Select project manager"
                    filter
                    showClear
                  />
                </div>

                <div className="field">
                  <label htmlFor="participantEmails">Participants</label>
                  <MultiSelect
                    value={formik.values.participantEmails}
                    options={userOptions}
                    onChange={(e) =>
                      formik.setFieldValue("participantEmails", e.value)
                    }
                    placeholder="Select participants"
                    filter
                    showClear
                  />
                </div>

                <Button
                  icon="pi pi-check"
                  type="submit"
                  label="Create Project"
                  className="p-mt-2"
                  loading={creatingUnlinkedProject}
                />
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ProjectCreate;
